const ON_CHANGE_DEBOUNCE_TIMER = 300;

const PUB_SUB_EVENTS = {
  cartUpdate: 'cart-update',
  quantityUpdate: 'quantity-update',
  optionValueSelectionChange: 'option-value-selection-change',
  variantChange: 'variant-change',
  cartError: 'cart-error',
};

/**
 * TZN checkout safeguard
 * Enforces the 19 EUR minimum order value on every storefront checkout entry point.
 * The customer-facing minimum-order message is rendered by the existing cart UI;
 * this safeguard only blocks checkout and accelerated checkout bypasses.
 */
(() => {
  const MINIMUM_ORDER_VALUE = 1900;
  const CART_CHECKOUT_BUTTONS = '#checkout, #CartDrawer-Checkout';
  const CART_EXPRESS_CHECKOUT = '.additional-checkout-buttons';
  const PDP_EXPRESS_CHECKOUT = '.shopify-payment-button, [data-shopify="payment-button"]';

  let minimumOrderBlocked = null;
  let syncTimer;
  let syncing = false;

  function setElementHidden(element, hidden, marker) {
    if (!element) return;

    if (hidden) {
      if (!element.hasAttribute(marker)) {
        element.setAttribute(marker, 'true');
      }
      element.style.display = 'none';
      element.setAttribute('aria-hidden', 'true');
    } else if (element.getAttribute(marker) === 'true') {
      element.style.removeProperty('display');
      element.removeAttribute('aria-hidden');
      element.removeAttribute(marker);
    }
  }

  function disablePdpExpressCheckout() {
    document.querySelectorAll(PDP_EXPRESS_CHECKOUT).forEach((element) => {
      setElementHidden(element, true, 'data-tzn-pdp-express-disabled');
    });
  }

  function removeDuplicateMinimumOrderMessage() {
    document.querySelectorAll('.tzn-minimum-order-message').forEach((message) => message.remove());
  }

  function applyMinimumOrderState(cart) {
    const itemCount = Number(cart?.item_count || 0);
    const totalPrice = Number(cart?.total_price || 0);
    const isBlocked = itemCount > 0 && totalPrice < MINIMUM_ORDER_VALUE;

    minimumOrderBlocked = isBlocked;
    removeDuplicateMinimumOrderMessage();

    document.querySelectorAll(CART_CHECKOUT_BUTTONS).forEach((button) => {
      button.disabled = itemCount === 0 || isBlocked;

      if (isBlocked) {
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('data-tzn-minimum-order-blocked', 'true');
      } else {
        button.removeAttribute('data-tzn-minimum-order-blocked');
        if (itemCount > 0) button.removeAttribute('aria-disabled');
      }
    });

    document.querySelectorAll(CART_EXPRESS_CHECKOUT).forEach((container) => {
      setElementHidden(container, isBlocked, 'data-tzn-minimum-order-hidden');
    });

    disablePdpExpressCheckout();
  }

  async function syncMinimumOrderGuard() {
    if (syncing) return;
    syncing = true;

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) throw new Error(`Cart request failed with status ${response.status}`);
      const cart = await response.json();
      applyMinimumOrderState(cart);
    } catch (error) {
      minimumOrderBlocked = true;
      removeDuplicateMinimumOrderMessage();
      document.querySelectorAll(CART_CHECKOUT_BUTTONS).forEach((button) => {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('data-tzn-minimum-order-blocked', 'true');
      });
      document.querySelectorAll(CART_EXPRESS_CHECKOUT).forEach((container) => {
        setElementHidden(container, true, 'data-tzn-minimum-order-hidden');
      });
      disablePdpExpressCheckout();
      console.warn('TZN checkout safeguard could not refresh the cart state.', error);
    } finally {
      syncing = false;
    }
  }

  function scheduleMinimumOrderSync(delay = 80) {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(syncMinimumOrderGuard, delay);
  }

  function shouldBlockCheckoutInteraction(target) {
    if (!target) return false;

    const cartCheckout = target.closest?.(
      `${CART_CHECKOUT_BUTTONS}, ${CART_EXPRESS_CHECKOUT}, a[href*="/checkout"]`
    );
    if (cartCheckout && minimumOrderBlocked !== false) return true;

    const pdpExpress = target.closest?.(PDP_EXPRESS_CHECKOUT);
    return Boolean(pdpExpress);
  }

  document.addEventListener(
    'click',
    (event) => {
      if (!shouldBlockCheckoutInteraction(event.target)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      scheduleMinimumOrderSync(0);
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      const isCartForm = form?.id === 'cart' || form?.id === 'CartDrawer-Form';
      const isCheckoutSubmit = event.submitter?.name === 'checkout';

      if (isCartForm && isCheckoutSubmit && minimumOrderBlocked !== false) {
        event.preventDefault();
        event.stopImmediatePropagation();
        scheduleMinimumOrderSync(0);
      }
    },
    true
  );

  if (document.body) {
    const cartSelector = 'cart-drawer, cart-items, cart-notification, #main-cart-footer, .cart__blocks, .shopify-payment-button, [data-shopify="payment-button"]';
    const observer = new MutationObserver(records => {
      const cartChanged = records.some(record => record.target instanceof Element && (record.target.closest(cartSelector) || [...record.addedNodes].some(node => node instanceof Element && (node.matches(cartSelector) || node.querySelector(cartSelector)))));
      if (!cartChanged) return;
      removeDuplicateMinimumOrderMessage();
      disablePdpExpressCheckout();
      scheduleMinimumOrderSync();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        removeDuplicateMinimumOrderMessage();
        disablePdpExpressCheckout();
        scheduleMinimumOrderSync(0);
      },
      { once: true }
    );
  } else {
    removeDuplicateMinimumOrderMessage();
    disablePdpExpressCheckout();
    scheduleMinimumOrderSync(0);
  }

  window.addEventListener('pageshow', () => scheduleMinimumOrderSync(0));
})();

/**
 * TZN mobile fix for the YOYO Loyalty drawer.
 * Keeps the storefront fixed while the rewards drawer is open and lets the drawer scroll itself.
 */
(() => {
  if (window.__tznYoyoMobileScrollFix) return;
  window.__tznYoyoMobileScrollFix = true;

  const mobile = window.matchMedia('(max-width: 990px)');
  let activePanel = null;
  let activeScrollRoot = null;
  let lockedScrollY = 0;
  let bodySnapshot = null;
  let refreshQueued = false;
  const panelStyles = new Map();

  const setPanelStyle = (element, property, value) => {
    if (!panelStyles.has(element)) panelStyles.set(element, new Map());
    const saved = panelStyles.get(element);
    if (!saved.has(property)) saved.set(property, {
      value: element.style.getPropertyValue(property),
      priority: element.style.getPropertyPriority(property)
    });
    element.style.setProperty(property, value, 'important');
  };

  const restorePanelStyles = () => {
    panelStyles.forEach((properties, element) => properties.forEach((saved, property) => {
      if (saved.value) element.style.setProperty(property, saved.value, saved.priority);
      else element.style.removeProperty(property);
    }));
    panelStyles.clear();
    delete document.documentElement.dataset.avLoyaltyOpen;
  };

  const isVisible = (el) => {
    if (!el || !el.isConnected) return false;
    const style = window.getComputedStyle(el);
    const rect = el.getBoundingClientRect();
    return style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 0 && rect.height > 0;
  };

  const findPanel = () => {
    const app = document.getElementById('clr-app');
    if (!app) return null;
    return Array.from(app.querySelectorAll('[class*="_loyalty_widget_wrapper_"]')).find(isVisible) || null;
  };

  const findScrollRoot = (panel) => {
    const layout = panel.querySelector('[class*="_widget_layout_wrapper_"]');
    if (isVisible(layout)) return layout;
    const candidates = [panel, ...panel.querySelectorAll('div, section, main')];
    let best = panel;
    let overflow = Math.max(0, panel.scrollHeight - panel.clientHeight);

    for (const el of candidates) {
      if (!isVisible(el)) continue;
      const amount = Math.max(0, el.scrollHeight - el.clientHeight);
      if (amount > overflow) {
        overflow = amount;
        best = el;
      }
    }
    return best;
  };

  const lockPage = () => {
    if (bodySnapshot) return;
    lockedScrollY = window.scrollY || window.pageYOffset || 0;
    bodySnapshot = {
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      width: document.body.style.width,
      overflow: document.body.style.overflow,
      htmlOverflow: document.documentElement.style.overflow
    };

    document.documentElement.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${lockedScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
  };

  const unlockPage = () => {
    if (!bodySnapshot) return;
    const snapshot = bodySnapshot;
    bodySnapshot = null;

    document.documentElement.style.overflow = snapshot.htmlOverflow;
    document.body.style.position = snapshot.position;
    document.body.style.top = snapshot.top;
    document.body.style.left = snapshot.left;
    document.body.style.right = snapshot.right;
    document.body.style.width = snapshot.width;
    document.body.style.overflow = snapshot.overflow;
    window.scrollTo(0, lockedScrollY);
  };

  const activatePanel = (panel) => {
    if (!mobile.matches || !panel) return;
    const firstActivation = panel !== activePanel;
    activePanel = panel;
    activeScrollRoot = findScrollRoot(panel);

    setPanelStyle(panel, 'max-height', '100dvh');
    document.documentElement.dataset.avLoyaltyOpen = 'true';
    setPanelStyle(panel, 'overscroll-behavior-y', 'contain');

    if (activeScrollRoot) {
      setPanelStyle(activeScrollRoot, 'overflow-y', 'auto');
      setPanelStyle(activeScrollRoot, '-webkit-overflow-scrolling', 'touch');
      setPanelStyle(activeScrollRoot, 'overscroll-behavior-y', 'contain');
      setPanelStyle(activeScrollRoot, 'touch-action', 'pan-y');
    }

    lockPage();

    if (firstActivation) {
      requestAnimationFrame(() => {
        panel.scrollTop = 0;
        if (activeScrollRoot) activeScrollRoot.scrollTop = 0;
      });
    }
  };

  const refresh = () => {
    refreshQueued = false;
    if (!document.body) return;

    if (!mobile.matches) {
      restorePanelStyles();
      activePanel = null;
      activeScrollRoot = null;
      unlockPage();
      return;
    }

    const panel = findPanel();
    if (panel) {
      activatePanel(panel);
    } else if (activePanel) {
      restorePanelStyles();
      activePanel = null;
      activeScrollRoot = null;
      unlockPage();
    }
  };

  const queueRefresh = () => {
    if (refreshQueued) return;
    refreshQueued = true;
    window.requestAnimationFrame(refresh);
  };

  document.addEventListener('touchmove', (event) => {
    if (!activePanel) return;
    if (!activePanel.contains(event.target)) event.preventDefault();
  }, { passive: false, capture: true });

  document.addEventListener('wheel', (event) => {
    if (!activePanel) return;
    if (!activePanel.contains(event.target)) event.preventDefault();
  }, { passive: false, capture: true });

  const init = () => {
    const observer = new MutationObserver(queueRefresh);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'hidden']
    });

    mobile.addEventListener?.('change', queueRefresh);
    window.addEventListener('resize', queueRefresh, { passive: true });
    window.addEventListener('pageshow', queueRefresh);
    document.addEventListener('click', () => window.setTimeout(queueRefresh, 50), true);
    queueRefresh();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
